export interface LocationData {
  latitude: number;
  longitude: number;
  accuracy: number;
  timestamp: Date;
  error?: string;
}

/**
 * Capture current GPS location with timeout and error handling
 */
export const captureCurrentLocation = (): Promise<LocationData> => {
  return new Promise((resolve) => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      resolve({
        latitude: 0,
        longitude: 0,
        accuracy: 0,
        timestamp: new Date(),
        error: 'Geolocation not supported by browser'
      });
      return;
    }

    // Set up options for high accuracy
    const options: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000, // 10 seconds timeout
      maximumAge: 300000 // Accept cached position up to 5 minutes old
    };

    // Request current position
    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date(position.timestamp)
        });
      },
      (error) => {
        let errorMessage = 'Unknown location error';
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }

        resolve({
          latitude: 0,
          longitude: 0,
          accuracy: 0,
          timestamp: new Date(),
          error: errorMessage
        });
      },
      options
    );
  });
};

/**
 * Request location permission without getting the actual location
 */
export const requestLocationPermission = (): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => resolve(true),
      () => resolve(false),
      { enableHighAccuracy: false, timeout: 5000 }
    );
  });
};

/**
 * Check if two coordinates are within a certain distance (in meters)
 */
export const isWithinDistance = (
  lat1: number, 
  lon1: number, 
  lat2: number, 
  lon2: number, 
  maxDistance: number
): boolean => {
  const R = 6371e3; // Earth's radius in meters
  const φ1 = lat1 * Math.PI / 180;
  const φ2 = lat2 * Math.PI / 180;
  const Δφ = (lat2 - lat1) * Math.PI / 180;
  const Δλ = (lon2 - lon1) * Math.PI / 180;

  const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
          Math.cos(φ1) * Math.cos(φ2) *
          Math.sin(Δλ/2) * Math.sin(Δλ/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

  const distance = R * c;
  return distance <= maxDistance;
}; 